import React from "react";
import { useLang } from "../lib/LanguageContext";
import PageHeader from "../components/PageHeader";
import { Trophy, Award, Medal, Star, Crown, Sparkles } from "lucide-react";

const rankIcons = [Crown, Medal, Trophy];

const Achievements = () => {
  const { t, lang } = useLang();
  const p = t.achievementsPg;

  return (
    <div data-testid="achievements-page">
      <PageHeader eyebrow={t.nav.achievements} title={p.title} subtitle={p.sub} testId="achievements-header" />

      {/* Toppers grouped by year */}
      <section className="section-pad">
        <div className="container-school">
          <div className="max-w-3xl">
            <span className="eyebrow">{t.nav.achievements}</span>
            <h2 className="mt-3 font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-school-primary">{p.toppers.title}</h2>
            <p className="mt-4 text-base md:text-lg text-school-muted">{p.toppers.sub}</p>
          </div>

          <div className="mt-14 space-y-14">
            {p.toppers.years.map((yr, yi) => (
              <div key={yi} data-testid={`toppers-year-${yi}`}>
                {/* Year ribbon */}
                <div className="flex items-center gap-4 mb-8">
                  <div className="px-5 py-2 rounded-full bg-school-primary text-white font-heading font-extrabold text-lg shadow-md">
                    {yr.year}
                  </div>
                  <div className="h-px flex-1 bg-gradient-to-r from-school-secondary via-school-secondary/30 to-transparent" />
                  <Sparkles className="w-5 h-5 text-school-secondary" />
                </div>

                <div className="grid md:grid-cols-3 gap-5">
                  {yr.list.map((tp, i) => {
                    const Icon = rankIcons[i] || Trophy;
                    return (
                      <div
                        key={i}
                        className="relative card-soft p-7 overflow-hidden"
                        data-testid={`topper-card-${yi}-${i}`}
                      >
                        {/* Subtle gold corner accent */}
                        <div className="absolute -top-12 -right-12 w-36 h-36 rounded-full bg-gradient-to-br from-school-secondary/30 to-transparent" />
                        <div className="absolute top-4 right-4 w-1.5 h-12 rounded-full bg-school-secondary" />

                        <div className="relative">
                          <div className="flex items-center gap-3">
                            <div className="w-14 h-14 rounded-2xl bg-school-primary flex items-center justify-center shadow-md">
                              <Icon className="w-7 h-7 text-school-secondary" fill="currentColor" />
                            </div>
                            {tp.rank && (
                              <span className="text-xs font-heading font-extrabold uppercase tracking-[0.2em] text-school-accent">
                                {tp.rank}
                              </span>
                            )}
                          </div>

                          <h3 className="mt-6 font-heading font-bold text-lg md:text-xl text-school-text leading-snug">
                            {tp.name}
                          </h3>

                          <div className="mt-4 flex items-baseline gap-2">
                            <span className="font-heading font-extrabold text-4xl text-school-primary">{tp.marks}</span>
                            <span className="text-xs text-school-muted">
                              {lang === "mr" ? "गुण" : "score"}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Awards & Recognition Timeline */}
      <section className="section-pad bg-school-muted/50">
        <div className="container-school">
          <div className="max-w-3xl">
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl text-school-primary flex items-center gap-3 leading-tight">
              <Award className="w-9 h-9 text-school-secondary shrink-0" /> {p.awards.title}
            </h2>
            <p className="mt-4 text-base md:text-lg text-school-muted">{p.awards.sub}</p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-5">
            {p.awards.items.map((a, i) => (
              <div
                key={i}
                className="card-soft p-7 flex gap-5"
                data-testid={`award-item-${i}`}
              >
                {/* Year tile */}
                <div className="shrink-0 w-16 flex flex-col items-center">
                  <div className="w-14 h-14 rounded-2xl bg-school-secondary/15 flex items-center justify-center">
                    <Star className="w-6 h-6 text-school-secondary" fill="currentColor" />
                  </div>
                  <div className="mt-2 font-heading font-extrabold text-school-primary text-base text-center">
                    {a.year}
                  </div>
                </div>
                <div className="flex-1">
                  <p className="font-heading text-base md:text-lg text-school-text leading-relaxed">
                    {a.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Achievements;
